import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const emptyForm = {
  first_name: "",
  middle_name: "",
  last_name: "",
  email: "",
  username: "",
  phone: "",
  gender: "",
  date_of_birth: "",
  address: "",
  status: "ACTIVE",
  is_active: true,
  department: "",
  job_title: "",
  qualification: "",
  employment_date: "",
};

const emptyAllocation = {
  subject_type: "SPECIALIST",
  subject: "",
  class_subject: "",
  class_groups: [],
  term: "",
  is_lead_teacher: false,
  is_active: true,
};

const teacherFlowOptions = [
  {
    value: "CLASS_TEACHER",
    label: "Class Teacher",
    icon: "bi-person-badge",
    description: "Usually responsible for one class group and normal class-teacher subjects.",
  },
  {
    value: "SPECIALIST",
    label: "Specialist Teacher",
    icon: "bi-cpu",
    description: "Teaches specialist subjects across many grades, streams or class groups.",
  },
  {
    value: "BOTH",
    label: "Class & Specialist",
    icon: "bi-intersect",
    description: "Can be a class teacher and also teach specialist subjects.",
  },
];

const getSubjectTypeLabel = (value) => {
  if (value === "SPECIALIST") return "Specialist Subject";
  if (value === "CLASS_TEACHER") return "Class Teacher Subject";
  return "Subject";
};

const uniqueRows = (rows, field) => [
  ...new Set(rows.map((row) => row[field]).filter(Boolean)),
];

const inferTeacherFlowFromAllocations = (rows = []) => {
  const hasSpecialist = rows.some((row) => row.subject_type === "SPECIALIST");
  const hasClassTeacher = rows.some((row) => row.subject_type === "CLASS_TEACHER");

  if (hasSpecialist && hasClassTeacher) return "BOTH";
  if (hasSpecialist) return "SPECIALIST";
  return "CLASS_TEACHER";
};


const getErrorMessage = (data, fallback) => {
  if (data?.non_field_errors?.[0]?.includes("unique set")) {
    return "This teacher already has that class-subject for the selected term.";
  }

  return (
    data?.non_field_errors?.[0] ||
    data?.detail ||
    Object.values(data || {})?.flat?.()?.[0] ||
    fallback
  );
};

function TeacherEditModal({ teacher, onClose, onUpdated }) {
  const [formData, setFormData] = useState(emptyForm);
  const [allocationForm, setAllocationForm] = useState(emptyAllocation);
  const [teacherFlow, setTeacherFlow] = useState("SPECIALIST");
  const [classSubjects, setClassSubjects] = useState([]);
  const [terms, setTerms] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingAllocation, setSavingAllocation] = useState(false);

  useEffect(() => {
    if (!teacher) return;

    setFormData({
      first_name: teacher.first_name || "",
      middle_name: teacher.middle_name || "",
      last_name: teacher.last_name || "",
      email: teacher.email || "",
      username: teacher.username || "",
      phone: teacher.phone || "",
      gender: teacher.gender || "",
      date_of_birth: teacher.date_of_birth || "",
      address: teacher.address || "",
      status: teacher.is_active ? teacher.status || "ACTIVE" : "INACTIVE",
      is_active: Boolean(teacher.is_active),
      department: teacher.department || "",
      job_title: teacher.job_title || "",
      qualification: teacher.qualification || "",
      employment_date: teacher.employment_date || "",
    });
  }, [teacher]);

  const loadAcademicData = async () => {
    if (!teacher) return;

    try {
      const [classSubjectsResponse, termsResponse, allocationsResponse] =
        await Promise.all([
          axiosInstance.get("academic/class-subjects/"),
          axiosInstance.get("academic/terms/"),
          axiosInstance.get("academic/teacher-allocations/"),
        ]);

      setClassSubjects(classSubjectsResponse.data || []);
      setTerms(termsResponse.data || []);
      setAllocations(allocationsResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Could not load teacher academic allocations.");
    }
  };

  useEffect(() => {
    loadAcademicData();
  }, [teacher]);

  const teacherAllocations = useMemo(
    () =>
      allocations.filter((allocation) => String(allocation.teacher) === String(teacher?.id)),
    [allocations, teacher]
  );

  useEffect(() => {
    if (teacherAllocations.length > 0) {
      setTeacherFlow(inferTeacherFlowFromAllocations(teacherAllocations));
    }
  }, [teacherAllocations]);

  const visibleClassSubjects = useMemo(() => {
    if (teacherFlow === "SPECIALIST") {
      return classSubjects.filter((item) => item.subject_type === "SPECIALIST");
    }
    if (teacherFlow === "CLASS_TEACHER") {
      return classSubjects.filter((item) => item.subject_type === "CLASS_TEACHER");
    }
    return classSubjects;
  }, [classSubjects, teacherFlow]);

  const availableSubjects = useMemo(() => {
    const grouped = new Map();
    visibleClassSubjects.forEach((item) => {
      if (!grouped.has(item.subject)) {
        grouped.set(item.subject, {
          id: item.subject,
          name: item.subject_name,
          code: item.subject_code,
          type: item.subject_type,
          typeLabel: item.subject_type_label || getSubjectTypeLabel(item.subject_type),
        });
      }
    });
    return Array.from(grouped.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [visibleClassSubjects]);

  const matchingClassSubjects = useMemo(() => {
    if (!allocationForm.subject) return [];
    return visibleClassSubjects.filter((item) => String(item.subject) === String(allocationForm.subject));
  }, [visibleClassSubjects, allocationForm.subject]);

  const selectedClassSubject = useMemo(
    () => classSubjects.find((item) => String(item.id) === String(allocationForm.class_subject)),
    [classSubjects, allocationForm.class_subject]
  );

  if (!teacher) return null;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => {
      const nextForm = { ...prev, [name]: type === "checkbox" ? checked : value };
      if (name === "is_active" && !checked) nextForm.status = "INACTIVE";
      if (name === "is_active" && checked && prev.status === "INACTIVE") {
        nextForm.status = "ACTIVE";
      }
      if (name === "status") nextForm.is_active = value !== "INACTIVE";
      return nextForm;
    });
  };

  const handleAllocationChange = (event) => {
    const { name, value, type, checked, selectedOptions } = event.target;

    if (name === "class_groups") {
      const selectedValues = Array.from(selectedOptions || []).map((option) => option.value);
      setAllocationForm((prev) => ({ ...prev, class_groups: selectedValues }));
      return;
    }

    setAllocationForm((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      if (name === "subject_type") {
        next.subject = "";
        next.class_subject = "";
        next.class_groups = [];
      }
      if (name === "subject") {
        next.class_subject = "";
        next.class_groups = [];
      }
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim()) {
      toast.warning("First name, last name and email are required.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        subjects_taught: uniqueRows(teacherAllocations, "subject_name").join(", "),
        classes_assigned: uniqueRows(teacherAllocations, "class_group_name").join(", "),
        status:
          formData.is_active && formData.status === "INACTIVE"
            ? "ACTIVE"
            : formData.is_active
            ? formData.status
            : "INACTIVE",
        is_active: formData.status !== "INACTIVE" && formData.is_active,
      };

      const response = await axiosInstance.put(`teachers/${teacher.id}/`, payload);
      toast.success("Teacher updated successfully.");
      onUpdated(response.data);
    } catch (error) {
      console.error(error);
      const data = error.response?.data;
      toast.error(getErrorMessage(data, "Failed to update teacher."));
    } finally {
      setSaving(false);
    }
  };

  const handleAddAllocation = async (event) => {
    event.preventDefault();

    if (!allocationForm.term) {
      toast.warning("Please select the term.");
      return;
    }

    const selectedRows = [];
    const specialistMode = teacherFlow === "SPECIALIST" || teacherFlow === "BOTH";

    if (specialistMode && allocationForm.subject_type !== "CLASS_TEACHER") {
      if (!allocationForm.subject) {
        toast.warning("Please select the specialist subject.");
        return;
      }
      if (!allocationForm.class_groups.length) {
        toast.warning("Please select at least one class group.");
        return;
      }
      allocationForm.class_groups.forEach((classGroupId) => {
        const match = matchingClassSubjects.find(
          (item) => String(item.class_group) === String(classGroupId)
        );
        if (match) selectedRows.push(match);
      });
    } else {
      if (!allocationForm.class_subject) {
        toast.warning("Please select class subject and term.");
        return;
      }
      if (selectedClassSubject) selectedRows.push(selectedClassSubject);
    }

    if (selectedRows.length === 0) {
      toast.warning("No matching class subjects were found. Create them in Academic Setup first.");
      return;
    }

    const rowsToPost = selectedRows.filter(
      (classSubject) =>
        !teacherAllocations.some(
          (allocation) =>
            String(allocation.class_subject) === String(classSubject.id) &&
            String(allocation.term) === String(allocationForm.term)
        )
    );

    if (rowsToPost.length === 0) {
      toast.warning("Those allocations already exist for this teacher and term.");
      return;
    }

    try {
      setSavingAllocation(true);
      await Promise.all(
        rowsToPost.map((classSubject) =>
          axiosInstance.post("academic/teacher-allocations/", {
            teacher: teacher.id,
            class_subject: classSubject.id,
            term: allocationForm.term,
            is_lead_teacher: allocationForm.is_lead_teacher,
            is_active: allocationForm.is_active,
          })
        )
      );
      toast.success(rowsToPost.length === 1 ? "Teacher allocation added." : `${rowsToPost.length} teacher allocations added.`);
      setAllocationForm(emptyAllocation);
      await loadAcademicData();
    } catch (error) {
      console.error(error);
      const data = error.response?.data;
      toast.error(getErrorMessage(data, "Failed to add teacher allocation."));
    } finally {
      setSavingAllocation(false);
    }
  };

  const removeAllocation = async (allocation) => {
    if (!window.confirm(`Remove ${allocation.class_group_name} - ${allocation.subject_name}?`)) {
      return;
    }

    try {
      await axiosInstance.delete(`academic/teacher-allocations/${allocation.id}/`);
      toast.success("Teacher allocation removed.");
      await loadAcademicData();
    } catch (error) {
      console.error(error);
      toast.error("Could not remove allocation.");
    }
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">Edit Teacher</h5>
              <small className="text-muted">{teacher.staff_number}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <div className="alert alert-light border d-flex flex-wrap justify-content-between gap-2">
                <div>
                  <strong>Academic Allocations</strong>
                  <div className="text-muted small">
                    Use Class Teacher for one class responsibility, or Specialist Teacher for bulk subject allocations across many grades and streams.
                  </div>
                </div>
                <div className="text-end">
                  <small className="text-muted d-block">Current Allocations</small>
                  <strong>{teacherAllocations.length}</strong>
                </div>
              </div>

              <div className="row g-2 mb-3">
                {teacherFlowOptions.map((option) => (
                  <div className="col-md-4" key={option.value}>
                    <button
                      type="button"
                      className={`btn w-100 h-100 text-start border ${teacherFlow === option.value ? "btn-success" : "btn-light"}`}
                      onClick={() => {
                        setTeacherFlow(option.value);
                        setAllocationForm({
                          ...emptyAllocation,
                          subject_type: option.value === "CLASS_TEACHER" ? "CLASS_TEACHER" : "SPECIALIST",
                        });
                      }}
                    >
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className={`bi ${option.icon}`}></i>
                        <strong>{option.label}</strong>
                      </div>
                      <small className={teacherFlow === option.value ? "text-white-50" : "text-muted"}>{option.description}</small>
                    </button>
                  </div>
                ))}
              </div>

              <div className="row g-3">
                <div className="col-md-4"><label className="form-label">First Name</label><input name="first_name" className="form-control" value={formData.first_name} onChange={handleChange} required /></div>
                <div className="col-md-4"><label className="form-label">Middle Name</label><input name="middle_name" className="form-control" value={formData.middle_name} onChange={handleChange} /></div>
                <div className="col-md-4"><label className="form-label">Last Name</label><input name="last_name" className="form-control" value={formData.last_name} onChange={handleChange} required /></div>
                <div className="col-md-6"><label className="form-label">Email</label><input type="email" name="email" className="form-control" value={formData.email} onChange={handleChange} required /></div>
                <div className="col-md-6"><label className="form-label">Username / Employee Number</label><input name="username" className="form-control" value={formData.username} onChange={handleChange} /></div>
                <div className="col-md-4"><label className="form-label">Phone</label><input name="phone" className="form-control" value={formData.phone} onChange={handleChange} /></div>
                <div className="col-md-4"><label className="form-label">Gender</label><select name="gender" className="form-select" value={formData.gender} onChange={handleChange}><option value="">Select</option><option value="MALE">Male</option><option value="FEMALE">Female</option><option value="OTHER">Other</option></select></div>
                <div className="col-md-4"><label className="form-label">Date of Birth</label><input type="date" name="date_of_birth" className="form-control" value={formData.date_of_birth} onChange={handleChange} /></div>
                <div className="col-md-6"><label className="form-label">Department</label><input name="department" className="form-control" value={formData.department} onChange={handleChange} /></div>
                <div className="col-md-6"><label className="form-label">Job Title</label><input name="job_title" className="form-control" value={formData.job_title} onChange={handleChange} /></div>
                <div className="col-md-6"><label className="form-label">Qualification</label><input name="qualification" className="form-control" value={formData.qualification} onChange={handleChange} /></div>
                <div className="col-md-6"><label className="form-label">Employment Date</label><input type="date" name="employment_date" className="form-control" value={formData.employment_date} onChange={handleChange} /></div>

                <div className="col-12">
                  <hr />
                  <h6 className="fw-bold mb-2" style={{ color: "#125b2d" }}>Teaching Allocations</h6>
                  <div className="table-responsive border rounded-3">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Class</th>
                          <th>Subject</th>
                          <th>Type</th>
                          <th>Term</th>
                          <th>Lead</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherAllocations.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center text-muted py-3">
                              No academic allocations yet.
                            </td>
                          </tr>
                        ) : (
                          teacherAllocations.map((allocation) => (
                            <tr key={allocation.id}>
                              <td>{allocation.class_group_name}</td>
                              <td>{allocation.subject_code ? `${allocation.subject_code} - ` : ""}{allocation.subject_name}</td>
                              <td>
                                <span className={`badge ${allocation.subject_type === "SPECIALIST" ? "text-bg-info" : "text-bg-light border text-dark"}`}>
                                  {allocation.subject_type_label || getSubjectTypeLabel(allocation.subject_type)}
                                </span>
                              </td>
                              <td>{allocation.academic_year_name} - {allocation.term_name}</td>
                              <td>{allocation.is_lead_teacher ? "Yes" : "No"}</td>
                              <td>
                                <span className={`badge ${allocation.is_active ? "text-bg-success" : "text-bg-secondary"}`}>
                                  {allocation.is_active ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td className="text-end">
                                <button type="button" className="btn btn-sm btn-light border" title="Remove allocation" onClick={() => removeAllocation(allocation)}>
                                  <i className="bi bi-trash text-danger"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {teacherFlow === "BOTH" && (
                  <div className="col-md-4">
                    <label className="form-label">Allocation Type</label>
                    <select name="subject_type" className="form-select" value={allocationForm.subject_type} onChange={handleAllocationChange}>
                      <option value="SPECIALIST">Specialist Subject</option>
                      <option value="CLASS_TEACHER">Class Teacher Subject</option>
                    </select>
                  </div>
                )}

                {(teacherFlow === "SPECIALIST" || teacherFlow === "BOTH") && allocationForm.subject_type !== "CLASS_TEACHER" ? (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">Specialist Subject</label>
                      <select name="subject" className="form-select" value={allocationForm.subject} onChange={handleAllocationChange}>
                        <option value="">Select subject</option>
                        {availableSubjects.filter((subject) => subject.type === "SPECIALIST").map((subject) => (
                          <option key={subject.id} value={subject.id}>{subject.code ? `${subject.code} - ` : ""}{subject.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">Class Groups / Streams</label>
                      <select name="class_groups" className="form-select" multiple size="6" value={allocationForm.class_groups} onChange={handleAllocationChange} disabled={!allocationForm.subject}>
                        {matchingClassSubjects.map((item) => (
                          <option key={item.id} value={item.class_group}>{item.academic_year_name} - {item.class_group_name}</option>
                        ))}
                      </select>
                      <small className="text-muted">Hold Ctrl to select many class groups.</small>
                    </div>
                  </>
                ) : (
                  <div className="col-md-5">
                    <label className="form-label">Class Subject</label>
                    <select name="class_subject" className="form-select" value={allocationForm.class_subject} onChange={handleAllocationChange}>
                      <option value="">Select class subject</option>
                      {visibleClassSubjects.filter((item) => item.subject_type === "CLASS_TEACHER").map((item) => (
                        <option key={item.id} value={item.id}>{item.academic_year_name} - {item.class_group_name} - {item.subject_name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="col-md-3">
                  <label className="form-label">Term</label>
                  <select name="term" className="form-select" value={allocationForm.term} onChange={handleAllocationChange}>
                    <option value="">Select term</option>
                    {terms.map((term) => (
                      <option key={term.id} value={term.id}>{term.academic_year_name} - {term.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <label className="form-check mb-2">
                    <input type="checkbox" name="is_lead_teacher" className="form-check-input" checked={allocationForm.is_lead_teacher} onChange={handleAllocationChange} />
                    <span className="form-check-label">Lead</span>
                  </label>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button type="button" className="btn btn-outline-success w-100" disabled={savingAllocation} onClick={handleAddAllocation}>{savingAllocation ? "Adding..." : "Add"}</button>
                </div>

                <div className="col-md-6"><label className="form-label">Status</label><select name="status" className="form-select" value={formData.status} onChange={handleChange}><option value="ACTIVE">Active</option><option value="PENDING">Pending</option><option value="SUSPENDED">Suspended</option><option value="INACTIVE">Inactive</option></select></div>
                <div className="col-md-6 d-flex align-items-end"><label className="form-check mb-2"><input type="checkbox" name="is_active" className="form-check-input" checked={formData.is_active} onChange={handleChange} /><span className="form-check-label">Teacher account is active</span></label></div>
                <div className="col-12"><label className="form-label">Address</label><textarea name="address" rows="2" className="form-control" value={formData.address} onChange={handleChange}></textarea></div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-light" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-success" disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TeacherEditModal;
